import { Monitor, Moon, Sun } from "lucide-react";
import { useTheme, type ThemeMode } from "@/lib/theme";
import { cn } from "@/lib/cn";

const MODES: { id: ThemeMode; icon: typeof Sun; label: string }[] = [
  { id: "light", icon: Sun, label: "Light" },
  { id: "dark", icon: Moon, label: "Dark" },
  { id: "system", icon: Monitor, label: "System" },
];

export function ThemeToggle({ compact = false }: { compact?: boolean }) {
  const { mode, setMode } = useTheme();

  if (compact) {
    const next: ThemeMode = mode === "light" ? "dark" : mode === "dark" ? "system" : "light";
    const Icon = MODES.find((m) => m.id === mode)?.icon ?? Monitor;
    return (
      <button
        onClick={() => setMode(next)}
        title={`Theme: ${mode} — click for ${next}`}
        className="grid size-9 place-items-center rounded-lg border border-border bg-background hover:bg-secondary transition-colors"
        aria-label="Toggle theme"
      >
        <Icon className="size-4" />
      </button>
    );
  }

  return (
    <div role="radiogroup" className="inline-flex rounded-full border border-border bg-background p-0.5">
      {MODES.map(({ id, icon: Icon, label }) => (
        <button
          key={id}
          role="radio"
          aria-checked={mode === id}
          onClick={() => setMode(id)}
          title={label}
          className={cn(
            "grid size-7 place-items-center rounded-full transition-colors",
            mode === id
              ? "bg-foreground text-background"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          <Icon className="size-3.5" />
        </button>
      ))}
    </div>
  );
}
